// store/challengeStore.ts
import { defineStore } from 'pinia';

import api from '@/services/api';
import ChallengeService from '@/services/ChallengeService';
import { Challenge, ChallengeStatus } from '@/types/Challenge';
import { useLikeStore } from './likeStore';

interface ChallengeStoreState {
    challenges: { [PK: string]: Challenge };
    userChallenges: { [user_id: string]: Challenge[] };
    uploadProgressByPostId: Record<string, number>;
    processingStartByPostId: Record<string, number>;
    feedChallengeIds: string[];
    lastEvaluatedKey: any; // Adjust type as needed
}

export const useChallengeStore = defineStore('challengeStore', {
    state: (): ChallengeStoreState => ({
        challenges: {},
        userChallenges: {},

        uploadProgressByPostId: {},
        processingStartByPostId: {},
        feedChallengeIds: [] as string[],
        lastEvaluatedKey: null as any,
    }),
    persist: {
        storage: localStorage,
    },
    actions: {
        add(challenge_list: Challenge[]) {
            console.log('[DEBUG] Adding challenges to store:', challenge_list);
            if (!this.challenges) this.challenges = {};

            challenge_list.forEach((challenge) => {
                if (!challenge?.PK) return;

                const PK = challenge.PK.startsWith('CHALLENGE#')
                    ? challenge.PK
                    : `CHALLENGE#${challenge.PK}`;

                // merge with existing
                const prev = this.challenges[PK] ?? {};
                const next: any = { ...prev, ...challenge };

                // single place to set the default, no duplication
                if (next.objectFit == null) next.objectFit = 'contain';

                // keep fields that came in; don't erase existing with null/undefined
                for (const [k, v] of Object.entries(next)) {
                    if (v === undefined || v === null) next[k] = (prev as any)[k];
                }

                this.challenges[PK] = next;

                console.log('[DEBUG] - Added/merged challenge in store:', PK, {
                    hadFit: (prev as any)?.objectFit,
                    newFit: next.objectFit,
                });
            });
        },

        get: async function (PK: string, force = false): Promise<Challenge | undefined> {
            if (!this.challenges) this.challenges = {};

            if (!PK.startsWith('CHALLENGE#')) {
                PK = `CHALLENGE#${PK}`;
            }

            if (!force && this.challenges[PK]) {
                return this.challenges[PK];
            }

            if (PK.startsWith('CHALLENGE#PLACEHOLDER')) {
                return undefined;
            }

            try {
                let challenge_id = PK;
                if (PK.startsWith('CHALLENGE#')) {
                    challenge_id = PK.split('#')[1];
                }
                const challenge = await ChallengeService.getChallenge(challenge_id);
                console.log('challengeStore get post from API:', challenge);
                if (challenge) {
                    return challenge;
                } else {
                    console.warn('Post not found from API:', PK);
                }
            } catch (err) {
                console.error('Failed to fetch post from API:', err);
            }

            return undefined;
        },

        // Update specific properties of a post
        update(PK: string, updates: Partial<Challenge>) {
            if (!PK.startsWith('CHALLENGE#')) {
                PK = `CHALLENGE#${PK}`;
            }
            const existing = this.challenges[PK] || {};
            const cleaned = Object.fromEntries(
                Object.entries(updates).filter(([_, v]) => v !== '' && v !== undefined)
            );
            this.challenges[PK] = {
                ...existing,
                ...cleaned,
            };
        },

        async publish(PK: string) {
            if (!PK.startsWith('CHALLENGE#')) {
                PK = `CHALLENGE#${PK}`;
            }

            const challenge = this.challenges[PK];
            if (!challenge) {
                console.warn(`Cannot publish. No challenge found for PK: ${PK}`);
                return;
            }

            challenge.status = ChallengeStatus.PUBLISHED;
            this.challenges[PK] = challenge;

            try {
                await ChallengeService.updateChallenge(PK, challenge);
                console.log(`Challenge ${PK} published.`);
            } catch (error) {
                console.error(`Failed to publish challenge ${PK}:`, error);
            }
        },

        async fetchInitialFeedChallenges(challenge_id: string | null = null) {
            console.log('[DEBUG] fetchInitialFeedChallenges', { challenge_id });

            try {
                const result = await ChallengeService.getRecentChallenges(10000); // NOTE LIMIT Is NOT USED BY BACKEND
                const recent = result.challenges || [];
                const likeStore = useLikeStore();

                // Add all fetched challenges
                this.add(recent);
                likeStore.addUserLikes(result.user_likes || []);
                this.lastEvaluatedKey = result.last_evaluated_key;

                // Normalize IDs
                let ids = recent.map((c) =>
                    c.PK.startsWith('CHALLENGE#') ? c.PK : `CHALLENGE#${c.PK}`
                );

                // Ensure challenge_id (from route) is present and first
                if (challenge_id) {
                    const fullId = challenge_id.startsWith('CHALLENGE#')
                        ? challenge_id
                        : `CHALLENGE#${challenge_id}`;

                    const inStore = !!this.challenges[fullId];
                    const inList = ids.includes(fullId);

                    if (!inStore && !inList) {
                        try {
                            const fetched = await ChallengeService.getChallenge(fullId);
                            if (fetched) {
                                this.add([fetched]);
                                console.log(
                                    '[DEBUG] Explicitly fetched missing challenge_id:',
                                    fullId
                                );
                            }
                        } catch (err) {
                            console.warn('[DEBUG] Failed to fetch challenge_id:', fullId, err);
                        }
                    }

                    // Move challenge_id to front if we now have it
                    if (this.challenges[fullId]) {
                        ids = [fullId, ...ids.filter((x) => x !== fullId)];
                    } else {
                        console.warn(
                            '[DEBUG] Could not ensure challenge_id at top of list:',
                            fullId
                        );
                    }
                }

                this.feedChallengeIds = ids;
                console.log('[DEBUG] Final feedChallengeIds:', ids);
            } catch (err) {
                console.error('[challengeStore] Failed to fetch initial feed challenges:', err);
            }
        },

        async fetchMoreFeedChallenges(limit = 10) {
            if (!this.lastEvaluatedKey) return;

            try {
                const result = await ChallengeService.getRecentChallenges(
                    limit,
                    this.lastEvaluatedKey
                );
                this.add(result.challenges);
                this.lastEvaluatedKey = result.last_evaluated_key;

                for (const challenge of result.challenges) {
                    const pk = challenge.PK.startsWith('CHALLENGE#')
                        ? challenge.PK
                        : `CHALLENGE#${challenge.PK}`;
                    if (!this.feedChallengeIds.includes(pk)) {
                        this.feedChallengeIds.push(pk);
                    }
                }

                // Optional: cap memory
                if (this.feedChallengeIds.length > 30) {
                    const removed = this.feedChallengeIds.splice(
                        0,
                        this.feedChallengeIds.length - 30
                    );
                    for (const pk of removed) {
                        delete this.challenges[pk];
                    }
                }
            } catch (err) {
                console.error('Failed to fetch more feed posts:', err);
            }
        },

        getNextChallengeId(currentPK: string): string | null {
            const index = this.feedChallengeIds.indexOf(currentPK);
            if (index >= 0 && index < this.feedChallengeIds.length - 1) {
                return this.feedChallengeIds[index + 1];
            }
            return null;
        },

        getPrevChallengeId(currentPK: string): string | null {
            const index = this.feedChallengeIds.indexOf(currentPK);
            if (index > 0) {
                return this.feedChallengeIds[index - 1];
            }
            return null;
        },

        getCurrentChallenge(challegeId: string): Challenge | undefined {
            if (!challegeId.startsWith('CHALLENGE#')) {
                challegeId = `CHALLENGE#${challegeId}`;
            }
            return this.challenges[challegeId];
        },
        // Set upload progress for a post
        setUploadProgress(challengeId: string, progress: number) {
            if (!challengeId.startsWith('CHALLENGE#')) {
                challengeId = `CHALLENGE#${challengeId}`;
            }
            this.uploadProgressByPostId[challengeId] = progress;

            if (progress >= 100 && !this.processingStartByPostId[challengeId]) {
                this.processingStartByPostId[challengeId] = Date.now();
            }
        },

        // Get upload progress for a post
        getUploadProgress(challengeId: string): number {
            if (!challengeId.startsWith('CHALLENGE#')) {
                challengeId = `CHALLENGE#${challengeId}`;
            }
            return this.uploadProgressByPostId[challengeId] || 0;
        },

        setProcessingStart(challengeId: string) {
            if (!challengeId.startsWith('CHALLENGE#')) {
                challengeId = `CHALLENGE#${challengeId}`;
            }
            if (!this.processingStartByPostId[challengeId]) {
                this.processingStartByPostId[challengeId] = Date.now();
            }
        },

        getProcessingStart(challengeId: string): number {
            if (!challengeId.startsWith('CHALLENGE#')) {
                challengeId = `CHALLENGE#${challengeId}`;
            }
            return this.processingStartByPostId[challengeId] ?? 0;
        },

        clearUploadProgress(challengeId: string) {
            delete this.uploadProgressByPostId[challengeId];
            delete this.processingStartByPostId[challengeId];
        },

        // LEGACY CHALLENGE STORE
        getUserChallenges(user_id: string, limit = 1000) {
            return ChallengeService.getUserChallenges(user_id, limit).then((challenges) => {
                this.userChallenges[user_id] = challenges;
                return challenges;
            });
        },
        // Add multiple challenges to the store by PK
        addChallenges(challenges: Challenge[]) {
            challenges.forEach((challenge) => {
                if (challenge.PK) {
                    this.challenges[challenge.PK] = challenge;
                }
            });
        },
        // Get a single challenge by its PK
        getChallenge(PK: string): Challenge | undefined {
            if (!PK) return undefined; // Guard against undefined
            if (!PK.includes('CHALLENGE#')) {
                PK = 'CHALLENGE#' + PK;
            }
            return this.challenges[PK];
        },

        // Remove a challenge by its PK
        removeChallenge(PK: string) {
            if (this.challenges[PK]) {
                delete this.challenges[PK];
            }
        },

        // Clear the entire challenge cache
        clearCache() {
            this.challenges = {};
        },

        // Update specific properties of a challenge (e.g., like or comment counts)
        updateChallenge(PK: string, updates: Partial<Challenge>) {
            if (this.challenges[PK]) {
                this.challenges[PK] = { ...this.challenges[PK], ...updates };
            }
        },
    },
});
